import { useEffect, useRef, useState } from 'react'

import { useUserProgress } from '@/shared/contexts/UserProgress'
import { api } from '@/shared/services/api'

export type AutoCompletableType =
  | 'Journey'
  | 'Stage'
  | 'Level'
  | 'Challenge'
  | 'Mission'
  | 'Document'
  | 'Cosmetic'
  | 'RewardCategory'

export interface AutoCompleteItem {
  type: string
  id: number
  name: string | null
}

export interface AutoCompleteBlockedItem extends AutoCompleteItem {
  reason: string
}

export type AutoCompleteStatus = 'pending' | 'processing' | 'completed' | 'failed'

export interface AutoCompleteRequest {
  id: number
  status: AutoCompleteStatus
  settled: boolean
  error_message: string | null
  completed: AutoCompleteItem[]
  blocked: AutoCompleteBlockedItem[]
}

interface AutoCompleteParams {
  type: AutoCompletableType
  id: string | number
}

const POLL_INTERVAL_MS = 2000

// Teto de segurança: concluir uma jornada longa leva segundos, não minutos. Sem
// isto, um job que morre sem fechar a solicitação deixaria o botão girando para
// sempre.
const POLL_TIMEOUT_MS = 5 * 60 * 1000

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Ferramenta de suporte do admin: dispara a auto-conclusão do alvo e acompanha
 * até fechar.
 *
 * É assíncrona porque a conclusão percorre a cadeia de pré-requisitos inteira,
 * desafio a desafio, com recompensa e voucher em cada um — não cabe no tempo de
 * uma requisição. O POST devolve a solicitação, este hook consulta o status a
 * cada 2s e só recarrega o progresso quando ela fecha.
 *
 * Nenhuma trava é contornada: o que não pôde ser satisfeito volta em `blocked`
 * com o motivo, e é esse relatório que diz por que o aluno não vê o conteúdo.
 */
export const useAutoComplete = () => {
  const { refetchProgress } = useUserProgress()

  const [isRunning, setIsRunning] = useState(false)
  const [status, setStatus] = useState<AutoCompleteStatus | null>(null)

  // Evita setState depois de o card sair da tela — o polling sobrevive à
  // navegação, o componente não.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const autoComplete = async ({
    type,
    id
  }: AutoCompleteParams): Promise<AutoCompleteRequest | null> => {
    setIsRunning(true)
    setStatus('pending')

    try {
      // 409 = já existe uma auto-conclusão em andamento para este usuário. A API
      // devolve a solicitação corrente junto, então o certo é acompanhar aquela
      // em vez de tratar como erro: o admin clicou duas vezes, não errou.
      const { data } = await api.post(
        '/user/progress/auto_complete',
        { record: { resource_type: type, resource_id: id } },
        { validateStatus: statusCode => statusCode === 202 || statusCode === 409 }
      )

      const requestId: number | undefined = data?.auto_complete?.id
      if (!requestId) return null

      const settled = await pollUntilSettled(requestId)

      // O progresso só é recarregado no fim: recarregar a cada volta do polling
      // faria a tela piscar estados intermediários da cadeia.
      await refetchProgress()

      return settled
    } finally {
      if (mountedRef.current) {
        setIsRunning(false)
      }
    }
  }

  const pollUntilSettled = async (
    requestId: number
  ): Promise<AutoCompleteRequest | null> => {
    const deadline = Date.now() + POLL_TIMEOUT_MS

    while (Date.now() < deadline) {
      await wait(POLL_INTERVAL_MS)

      const { data } = await api.get(`/user/progress/auto_complete/${requestId}`)
      const request: AutoCompleteRequest | undefined = data?.auto_complete

      if (!request) return null

      if (mountedRef.current) setStatus(request.status)

      if (request.settled) return request
    }

    throw new Error('auto_complete_timeout')
  }

  return { autoComplete, isRunning, status }
}
